import {
    SimulationStep,
    FormDataCLT,
    FinanceiraCard,
    SimulacaoParams,
    SimulacaoResult,
    OfertaSelecionada,
    FormDataDigitacao,
    PropostaResultado,
    DigitacaoFormField,
    PartnerCommissionResult,
    UltimaSimulacao,
} from '../models/SimulationBank/SimulationBankModels';

// Cache da sessão de simulação (a partir do envio do formulário) — sobrevive a um F5
// por até SESSION_CACHE_TTL, ou até o formulário ser reenviado (o que limpa o cache antigo).
const SESSION_CACHE_KEY = '@beviajuda:sim-session';
const SESSION_CACHE_TTL = 10 * 60 * 1000;

export interface SimulationSessionData {
    step: SimulationStep;
    formData: FormDataCLT | null;
    financeiras: FinanceiraCard[];
    screeningId: string | number | null;
    simulacaoId: string | number | null;
    resultados: SimulacaoResult[];
    simParams: SimulacaoParams | null;
    hasOptionalData: boolean;
    // SB-1953 — histórico devolvido pelo /consult, persistido pra o card "Última
    // Simulação" (Step4Params) sobreviver a um F5 no meio da jornada: o consult não é
    // refeito, então sem o cache o card sumiria da tela.
    ultimaSimulacao: UltimaSimulacao | null;
    clienteConhecido: boolean | null;
    ofertaSelecionada: OfertaSelecionada | null;
    formDataDigitacao: FormDataDigitacao | null;
    propostaResultado: PropostaResultado | null;
    digitacaoForm: DigitacaoFormField[] | null;
    digitacaoIdCadastro: string | null;
    // Agente de digitação validado no StepConfirmacao (quando o parceiro logado não é
    // comissionado) — persistido para não perder a validação num F5 dentro da mesma sessão.
    typingAgent: PartnerCommissionResult | null;
}

interface SimulationSessionCache extends SimulationSessionData {
    savedAt: number;
}

export const saveSimulationSession = (data: SimulationSessionData): void => {
    try {
        // Mantém o savedAt original enquanto a sessão ainda for válida, para que o cache
        // expire 10min após o início da sessão, não a cada atualização (ex.: polling).
        let savedAt = Date.now();
        const raw = localStorage.getItem(SESSION_CACHE_KEY);
        if (raw) {
            const prev: SimulationSessionCache = JSON.parse(raw);
            if (prev?.savedAt && Date.now() - prev.savedAt <= SESSION_CACHE_TTL) {
                savedAt = prev.savedAt;
            }
        }
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ ...data, savedAt }));
    } catch {}
};

export const loadSimulationSession = (): SimulationSessionCache | null => {
    try {
        const raw = localStorage.getItem(SESSION_CACHE_KEY);
        if (!raw) return null;
        const cache: SimulationSessionCache = JSON.parse(raw);
        if (!cache?.savedAt || Date.now() - cache.savedAt > SESSION_CACHE_TTL) {
            localStorage.removeItem(SESSION_CACHE_KEY);
            return null;
        }
        return cache;
    } catch {
        return null;
    }
};

export const clearSimulationSession = (): void => {
    try { localStorage.removeItem(SESSION_CACHE_KEY); } catch {}
};

export const hasValidSimulationSession = (): boolean => loadSimulationSession() !== null;

// Guarda qual assistant (feature de chat) estava aberto na tela de simulação, para que
// um F5 reabra o mesmo assistant em vez de cair no primeiro da lista.
const ACTIVE_ASSISTANT_CACHE_KEY = '@beviajuda:sim-active-assistant';

interface ActiveAssistantCache {
    assistantId: number;
    savedAt: number;
}

export const saveActiveSimulationAssistant = (assistantId: number): void => {
    try {
        localStorage.setItem(ACTIVE_ASSISTANT_CACHE_KEY, JSON.stringify({ assistantId, savedAt: Date.now() }));
    } catch {}
};

export const loadActiveSimulationAssistant = (): number | null => {
    try {
        const raw = localStorage.getItem(ACTIVE_ASSISTANT_CACHE_KEY);
        if (!raw) return null;
        const cache: ActiveAssistantCache = JSON.parse(raw);
        if (!cache?.savedAt || Date.now() - cache.savedAt > SESSION_CACHE_TTL) {
            localStorage.removeItem(ACTIVE_ASSISTANT_CACHE_KEY);
            return null;
        }
        return cache.assistantId;
    } catch {
        return null;
    }
};

export const clearActiveSimulationAssistant = (): void => {
    try { localStorage.removeItem(ACTIVE_ASSISTANT_CACHE_KEY); } catch {}
};
